// Components
// Libs
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

// API
import getDeliveryNote from '@/common/api/delivery-notes/getDeliveryNote';
import Snack from '@/common/components/molecules/Snack/Snack';
// Constants
import { ENDPOINT_DELIVERY_NOTES } from '@/common/constants';
import { useModalContext } from '@/common/contexts/ModalContext';
import useDeliveryNotesFormats from '@/common/hooks/delivery-notes/useDeliveryNotesFormats';

const useDeliveryNote = ({ deliveryNotes, disabled, isLoading, isSuccess }) => {
  const { t } = useTranslation();
  const {
    data: formats,
    isLoading: isLoadingDeliveryNotesFormats,
  } = useDeliveryNotesFormats();

  /**
   * Return the delivery notes formats
   * @returns {{}}
   */
  const deliveryNoteFormats = useMemo(
    () =>
      formats?.data?.map(({ id: value, label: key }) => ({
        key,
        value: value.toString(),
      })) || [],
    [formats],
  );

  /**
   * Return the delivery notes
   * @returns {*[]}
   */
  const deliveryNotesList = useMemo(
    () =>
      deliveryNotes?.map((item) => ({
        key: item,
        value: item.toString(),
      })) || [],
    [deliveryNotes],
  );

  const {
    mutate: mutateGetDeliveryNote,
    isLoading: isLoadingGetDeliveryNote,
    isSuccess: isSuccessGetDeliveryNote,
  } = useMutation(getDeliveryNote);

  const { setModalIsOpen } = useModalContext();

  const onDownloadDeliveryNote = (deliveryNoteId, orderId, formatId) => {
    mutateGetDeliveryNote(
      {
        queryKey: [ENDPOINT_DELIVERY_NOTES, deliveryNoteId, orderId, formatId],
      },
      {
        onSuccess: () => {
          setModalIsOpen(false);
        },
        onError: (error) => {
          if (error.response.status === 500) {
            toast.error(
              <Snack
                message={t('common:messages:forms.errors.server_error')}
              />,
            );
          } else {
            toast.error(
              <Snack message={t('common:messages:forms.errors.default')} />,
            );
          }
        },
      },
    );
  };

  /**
   * Check if action is disabled
   * @type {boolean}
   */
  const isDisabled = useMemo(
    () => ({
      loading:
        isLoading || isLoadingGetDeliveryNote || isLoadingDeliveryNotesFormats,
      disabled:
        isLoading ||
        isLoadingGetDeliveryNote ||
        isLoadingDeliveryNotesFormats ||
        disabled,
    }),
    [
      disabled,
      isLoading,
      isLoadingDeliveryNotesFormats,
      isLoadingGetDeliveryNote,
    ],
  );

  /**
   * return disabled if disabled or delivery note is blank or format is blank
   * @param {object} values
   */
  const onIsDisabled = useCallback(
    (values) => isDisabled.disabled || !values.deliveryNote || !values.format,
    [isDisabled],
  );

  return {
    deliveryNoteFormats,
    deliveryNotesList,
    isDisabled,
    isSuccess: isSuccess || isSuccessGetDeliveryNote,
    onDownloadDeliveryNote,
    onIsDisabled,
  };
};

export default useDeliveryNote;
